import {
  Autocomplete,
  Box,
  createFilterOptions,
  FormControl,
  Stack,
  styled,
  TextField,
  useThemeProps,
} from '@mui/material';
import type { ComponentsPropsList } from '@mui/material/styles';
import { FORM_FIELD_THEME_NAME } from '@onevision-resources/design-theme';
import { parseScheduleWindows } from '@provision/sidekick-utils';
import {
  MaterialValueEditor,
  MaterialValueEditorProps,
  MaterialValueSelector,
  MaterialValueSelectorProps,
} from '@react-querybuilder/material';
import { Dayjs } from 'dayjs';
import React, { ComponentProps, useEffect, useMemo, useState } from 'react';
import { FullField, TestID } from 'react-querybuilder';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { ScheduleConditionEditor } from 'src/components/ScheduleConditionEditor';
import {
  BehaviorConfigOption,
  FormControlType,
  RuleControlType,
  SelectorValue,
} from 'src/graphql';
import {
  ConditionType,
  DataConditionOperator,
} from 'src/graphql/entities/behavior/behavior-config/behavior-config.types';
import { isValidHttpUrl } from 'src/utils/url';
import { RulesDatePicker } from './components/rules-date-picker';
import { RulesDateTimePicker } from './components/rules-date-time-picker';
import { UseQueryOptions } from './use-rules-engine';

const groupedAutocompleteStyleProps = {
  componentsProps: {
    popper: { style: { width: 'fit-content', maxWidth: 'min(32rem, 90vw)' } },
  },
};

export type OptionValue = {
  label: string;
  group: string;
} & ({ value: string } | { value: number } | { value: boolean });

const EMPTY_OPTION_VALUE: OptionValue = {
  value: '',
  group: '',
  label: '',
};

const NO_FORM_FIELD_PROPS: ComponentsPropsList['OvFormField'] = {};

// Name set by react-querybuilder to all added conditions / actions under `field` value
export const RQB_NEW_FIELD_NAME = 'new';

export type CustomValueSelectorProps = MaterialValueSelectorProps & {
  value: FullField;
  options: OptionValue[];
  testID: string;
  handleOnChange: (value: OptionValue) => void;
};
export const CustomValueSelector = (
  /**
   *  Providing own typings for specific properties
   *  to narrow typings related to MUI components used for customization.
   */
  props: CustomValueSelectorProps,
): React.JSX.Element | null => {
  const { options, value } = props;
  const { labelAbove } = useThemeProps({
    props: NO_FORM_FIELD_PROPS,
    name: FORM_FIELD_THEME_NAME,
  });
  const option =
    useMemo(() => {
      return options.find((item) => item.value === value)!;
    }, [options, value]) || EMPTY_OPTION_VALUE;

  if (props.field === RQB_NEW_FIELD_NAME && !props.value) {
    return null;
  }

  // `fields` id is used for the attribute selector component in the query builder.
  if (props.testID === TestID.fields) {
    // react-querybuilder assigns `~` string to `props.value` for value editor component,
    // when it is mounted and no option has been specified yet. Probably, because it expects for
    // the component to be a text input.
    // We need to provide an empty string to the Autocomplete component to comply with the type of
    // options array and/or not omit this value completely, because in that case form control
    // would be changed from uncontrolled to controlled.
    const optionValue = typeof value === 'string' ? option : value;

    // Leaving empty placeholder option on top, and making the rest of the options
    // properly grouped (Autocomplete doesn't do it on it's own).
    const sortedOptions = [...options].sort((a, b) =>
      !a.group ? -1 : a.group.localeCompare(b.group),
    );

    return (
      <Autocomplete<OptionValue, false, true>
        size="small"
        options={sortedOptions}
        groupBy={(option) => option.group}
        value={optionValue}
        renderInput={(params) =>
          labelAbove ? (
            <>
              <FormFieldLabel
                htmlFor={params.id}
                sx={{ position: 'absolute', bottom: '100%', left: 0, whiteSpace: 'nowrap' }}
              >
                Attribute
              </FormFieldLabel>
              <TextField {...params} />
            </>
          ) : (
            <TextField {...params} label="Attribute" />
          )
        }
        disableClearable
        onChange={(_, value) => {
          props.handleOnChange(value);
        }}
        getOptionLabel={(option) => option?.label ?? ''}
        isOptionEqualToValue={(option, value) => option?.value === value.value}
        renderOption={(optionProps, option) => {
          const { key, ...liProps } = optionProps as React.HTMLAttributes<HTMLLIElement> & {
            key?: React.Key;
          };

          // the Autocomplete renders `option.group` as the section header, so drop the duplicated
          // group prefix from the item itself (e.g. "Conversation / Subject" -> "Subject").
          const itemLabel =
            option.group && option.label.startsWith(`${option.group} / `)
              ? option.label.slice(option.group.length + 3)
              : option.label;
          return (
            <Box
              component="li"
              key={key}
              {...liProps}
              title={option.label}
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '200px',
              }}
            >
              {itemLabel}
            </Box>
          );
        }}
        sx={{
          position: 'relative',
          minWidth: { xs: 0, sm: '12rem' },
        }}
        {...groupedAutocompleteStyleProps}
      />
    );
  }

  return (
    <MaterialValueSelector
      {...props}
      variant="outlined"
      sx={{
        // The v2 field box sets its own height, the one the Attribute field next to it has.
        height: labelAbove ? undefined : '2.5rem',
        flexShrink: 0,
        minWidth: { xs: 0, sm: '4rem' },
      }}
    />
  );
};

const FieldsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: '13rem',
  [theme.breakpoints.down('sm')]: {
    minWidth: 0,
    flexWrap: 'wrap',
  },
}));

const MULTIPLE_CHOICE_OPERATORS = [
  DataConditionOperator.IsOneOf,
  DataConditionOperator.IsNotOneOf,
  DataConditionOperator.In,
  DataConditionOperator.NotIn,
];

// An array-typed custom attribute (e.g. multi_select) carries an array write/read cast such as
// `::text[]` on its accessor and always edits as a multi-value picker. Conditions reach this via
// the multi-choice operators above, but an action has no operator, so detect it from the cast.
const ARRAY_CAST_ACCESSOR_PATTERN = /::\w+\[\]$/;

const filterOptions = createFilterOptions({
  stringify: (option?: BehaviorConfigOption) =>
    option ? [option.label, option.id].join(', ') : '',
});

export const CustomValueEditor = (
  props: Omit<MaterialValueEditorProps, 'value' | 'context'> & {
    field: SelectorValue;
    value: string | OptionValue | OptionValue[];
    operator: DataConditionOperator;
    context: { timezone?: string; queryOptions: UseQueryOptions };
  },
) => {
  const [inputValue, setInputValue] = useState('');

  /*
   ** Used to control if input default value was set.
   ** This is needed to fill dynamic options value when edit form is opened
   ** should happen only once
   */
  const [defaultInputValueSet, setDefaultInputValueSet] = useState(false);

  // Reset input value when field changes OR operator changes
  useEffect(() => {
    setInputValue('');
    setDefaultInputValueSet(false);
  }, [props.field?.value, props.operator]);

  const { sx } = getFieldStyles(props);
  const { timezone, queryOptions } = props.context;

  // set selected input value for rule builder dynamic options
  useEffect(() => {
    // Don't set default value if operator just changed (value might be stale)
    const shouldSetDefaultValue =
      props.value && !inputValue && !defaultInputValueSet && typeof props.value !== 'string';

    if (shouldSetDefaultValue) {
      setDefaultInputValueSet(true);
      const optionLabel = (props?.value as OptionValue)?.label;
      if (optionLabel) {
        setInputValue(optionLabel);
      }
    }
  }, [inputValue, props.value, props.operator, defaultInputValueSet]);

  const optionsData = (() => {
    if (!props.field || !props.field.optionsQuery) {
      return undefined;
    }
    return props.context.queryOptions[props.field.optionsQuery];
  })();

  const options = useMemo(() => {
    if (!props.field) return [];

    if (props.field.optionsQuery) {
      return optionsData?.options;
    }
    if (props.field.fieldOptions?.length) {
      return props.field.fieldOptions;
    }
    return [];
  }, [optionsData?.options, props.field]);

  const isLoadingOptions = Boolean(optionsData?.isLoading);

  if (!props.field) return null;

  if (props.field === RQB_NEW_FIELD_NAME && !props.value) {
    return null;
  }

  let onInputChange: null | ((query: string) => void) = null;

  if (typeof props.field.optionsQuery !== 'undefined') {
    const optionsData = queryOptions[props.field.optionsQuery];
    if ('onInputChange' in optionsData) {
      onInputChange = optionsData.onInputChange || null;
    }
  }

  if (
    props.field.uiType === FormControlType.Text ||
    props.field.uiType === FormControlType.Number
  ) {
    return (
      <FieldsContainer>
        {props.field.controlType === RuleControlType.ActionControl ? 'to ' : null}
        <MaterialValueEditor
          {...props}
          inputType={props.field.uiType === FormControlType.Number ? 'number' : undefined}
          extraProps={{ sx }}
        />
      </FieldsContainer>
    );
  }

  if (props.field.uiType === FormControlType.Url) {
    const urlValue = typeof props.value === 'string' ? props.value : '';
    const isInvalidUrl = urlValue.length > 0 && !isValidHttpUrl(urlValue);
    return (
      <FieldsContainer>
        {props.field.controlType === RuleControlType.ActionControl ? 'to ' : null}
        <TextField
          size="small"
          value={urlValue}
          error={isInvalidUrl}
          helperText={isInvalidUrl ? 'Enter a valid URL, e.g. https://example.com' : undefined}
          onChange={(event) => props.handleOnChange(event.target.value)}
          sx={sx}
        />
      </FieldsContainer>
    );
  }
  if (props.field.uiType === FormControlType.List) {
    const fieldAccessor = typeof props.field.value === 'string' ? props.field.value : '';
    const isMultipleChoiceSelect =
      MULTIPLE_CHOICE_OPERATORS.includes(props.operator) ||
      ARRAY_CAST_ACCESSOR_PATTERN.test(fieldAccessor);

    let optionValue = props.value;
    if (typeof optionValue === 'string' || optionValue === undefined || optionValue === null) {
      optionValue = isMultipleChoiceSelect ? [] : EMPTY_OPTION_VALUE;
    }

    // add key prop that changes when operator changes to force component re-render
    const componentKey = `autocomplete-${props.operator}-${props.field.value}`;

    return (
      <FieldsContainer>
        {props.field.controlType === RuleControlType.ActionControl ? 'to ' : null}
        <FormControl size="small">
          <Autocomplete
            key={componentKey}
            options={options ?? []}
            inputValue={inputValue}
            filterOptions={filterOptions}
            multiple={isMultipleChoiceSelect}
            filterSelectedOptions={isMultipleChoiceSelect}
            getOptionKey={(option) => String(option?.id ?? '')}
            getOptionLabel={(option) => option?.label ?? ''}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            limitTags={2}
            size="small"
            value={optionValue || null}
            renderInput={(params) => <TextField {...params} />}
            disableClearable
            loading={isLoadingOptions}
            loadingText="Loading options…"
            onInputChange={(_, value, reason) => {
              if (reason === 'input') {
                setInputValue?.(value);
                onInputChange?.(value);
              }
            }}
            onChange={(_, value) => {
              setInputValue((value as OptionValue)?.label ?? '');
              props.handleOnChange(value);
            }}
            sx={sx}
          />
        </FormControl>
      </FieldsContainer>
    );
  }

  if (props.field.uiType === FormControlType.Date) {
    return (
      <FieldsContainer>
        <FormControl size="small">
          <RulesDatePicker
            onChange={({ isRollingDate, value }) => {
              if (isRollingDate) {
                props.handleOnChange(value);
                return;
              }

              props.handleOnChange((value as Dayjs).toISOString());
            }}
            value={props.value as string}
            timezone={timezone}
          />
        </FormControl>
      </FieldsContainer>
    );
  }

  if (props.field.uiType === FormControlType.DateTime) {
    return (
      <FieldsContainer>
        {props.field.controlType === RuleControlType.ActionControl ? 'to ' : null}
        <FormControl size="small">
          <RulesDateTimePicker
            value={props.value as string}
            timezone={timezone}
            onChange={(isoValue) => props.handleOnChange(isoValue)}
          />
        </FormControl>
      </FieldsContainer>
    );
  }

  if (props.field.uiType === FormControlType.Textarea) {
    return (
      <FieldsContainer>
        {props.field.controlType === RuleControlType.ActionControl ? 'to ' : null}
        <MaterialValueEditor
          {...props}
          extraProps={{ sx, multiline: true, minRows: 3, maxRows: 8 }}
        />
      </FieldsContainer>
    );
  }

  return <MaterialValueEditor {...props} extraProps={{ sx }} />;
};

function getFieldStyles(props: { field: SelectorValue; operator: DataConditionOperator }): {
  sx: Record<string, unknown>;
} {
  if (
    [DataConditionOperator.Between, DataConditionOperator.NotBetween].includes(
      props.operator,
    ) &&
    props.field.uiType === FormControlType.Number
  ) {
    return { sx: { width: '5rem' } };
  }

  return { sx: { minWidth: { xs: 0, sm: '12rem' } } };
}

/** The rules builder's value editor: a Custom schedule row edits its windows in place. */
export const ConditionValueEditor = (props: ComponentProps<typeof CustomValueEditor>) => {
  if (props.field?.conditionType === ConditionType.CustomSchedule) {
    return (
      <ScheduleConditionEditor
        className={props.className}
        windows={parseScheduleWindows(props.value)}
        onChange={props.handleOnChange}
        markInvalid
      />
    );
  }

  return <CustomValueEditor {...props} />;
};
